import { location } from 'cdui-js';
import { postToApp, registerRNHandler } from '../../../../../../lib/rnBridge';
import { consoleLog } from '../../../../../../lib/utils';
import i18n from '../../../../../../i18n';

export const MAINLAND_RISK_SZCA_STEP = 13;
/** 大陆正常开户签名步（CompMaps step 16） */
export const MAINLAND_SIGNATURE_SZCA_STEP = 16;
export const SZCA_RETURN_QUERY_KEY = 'ca';
export const SZCA_RETURN_QUERY_VALUE = '1';

export type SzcaH5ReturnContext = 'riskInfo' | 'editRefusedRisk';

export type BuildSzcaH5ReturnUrlOptions = {
  context?: SzcaH5ReturnContext;
  step?: number;
};

const SZCA_AUTH_TIMEOUT_MS = 300000;
const EMBED_QUERY_KEYS = ['platform', 'embed', 'embedded', 'version', 'lang'] as const;

export type OpenSzcaAuthViaAppParams = {
  url: string;
  h5Url: string;
  busId: string | number;
  bizNo?: string;
  resId?: string;
  certDn?: string;
  step?: number;
};

/** 认证完成后 App / SZCA 回跳地址（保留内嵌 query） */
export function buildSzcaH5ReturnUrl(options?: BuildSzcaH5ReturnUrlOptions | number): string {
  const opts: BuildSzcaH5ReturnUrlOptions =
    typeof options === 'number' ? { context: 'riskInfo', step: options } : (options ?? { context: 'riskInfo' });

  const base = String(import.meta.env.VITE_BASE_URL || window.location.origin).replace(/\/$/, '');
  const params = new URLSearchParams();

  if (opts.context === 'editRefusedRisk') {
    params.set('mode', 'editRefused');
    params.set('phase', 'risk');
  } else {
    params.set('step', String(opts.step ?? MAINLAND_RISK_SZCA_STEP));
  }

  params.set(SZCA_RETURN_QUERY_KEY, SZCA_RETURN_QUERY_VALUE);

  const q = location.query as Record<string, string | undefined>;
  for (const key of EMBED_QUERY_KEYS) {
    const v = q[key];
    if (v != null && String(v) !== '') params.set(key, String(v));
  }

  const returnUrl = `${base}/open-progress?${params.toString()}`;
  consoleLog('[SZCA] buildSzcaH5ReturnUrl', { context: opts.context, step: opts.step, returnUrl }, 'all');
  return returnUrl;
}

/** URL 带 ca=1 表示 SZCA 人脸已回跳，可执行 findCertDown */
export function isSzcaReturnFromQuery() {
  const isReturn = location.query[SZCA_RETURN_QUERY_KEY] === SZCA_RETURN_QUERY_VALUE;
  consoleLog('[SZCA] isSzcaReturnFromQuery', { isReturn, query: location.query, href: window.location.href }, 'all');
  return isReturn;
}

const szcaResolvers = new Map<string, { resolve: () => void; reject: (e: Error) => void }>();
let szcaSeq = 0;
let bridgeRegistered = false;

function ensureSzcaAuthBridge() {
  if (bridgeRegistered) return;
  bridgeRegistered = true;

  registerRNHandler((msg) => {
    if (msg.type !== 'APP_TO_WEB_SzcaAuthDone') return;

    const payload = (msg.payload ?? {}) as {
      requestId?: string;
      ok?: boolean;
      message?: string;
      code?: string;
    };
    consoleLog('[SZCA] APP_TO_WEB_SzcaAuthDone', payload, 'all');
    const requestId = payload.requestId;
    if (!requestId) return;

    const resolver = szcaResolvers.get(requestId);
    if (!resolver) return;

    szcaResolvers.delete(requestId);
    if (payload.ok) {
      resolver.resolve();
      return;
    }

    resolver.reject(new Error(payload.message || i18n.OpenProgress.Risk.Szca.ToastFaceIncomplete));
  });
}

/**
 * App 内打开 SZCA 人脸页；完成后由原生跳回 h5Url 并下发 APP_TO_WEB_SzcaAuthDone。
 */
export function openSzcaAuthViaApp(params: OpenSzcaAuthViaAppParams): Promise<void> {
  ensureSzcaAuthBridge();

  return new Promise((resolve, reject) => {
    const requestId = `szca_${Date.now()}_${++szcaSeq}`;
    szcaResolvers.set(requestId, { resolve, reject });

    const postPayload = {
      requestId,
      url: params.url,
      h5Url: params.h5Url,
      busId: String(params.busId),
      bizNo: params.bizNo,
      resId: params.resId,
      certDn: params.certDn,
      step: params.step ?? MAINLAND_RISK_SZCA_STEP,
    };
    consoleLog('[SZCA] openSzcaAuthViaApp -> WEB_TO_APP_OpenSzcaAuth', postPayload, 'all');

    postToApp({
      type: 'WEB_TO_APP_OpenSzcaAuth',
      payload: postPayload,
      timestamp: Date.now(),
    });

    window.setTimeout(() => {
      if (!szcaResolvers.has(requestId)) return;
      szcaResolvers.delete(requestId);
      reject(new Error(i18n.OpenProgress.Risk.Szca.ToastFaceTimeout));
    }, SZCA_AUTH_TIMEOUT_MS);
  });
}
